import { Box, Grid, Typography, IconButton, Collapse, Tooltip, Button } from '@mui/material';
import React, { useState } from 'react';
import { fCurrency } from '../../../../utils/formatNumber';
import CloseIcon from '@mui/icons-material/Close';
import { dispatch } from '../../../../redux/store';
import {
  deleteGuestBilling,
  removeCardFromCheckin,
  removePaidPayment,
} from '../../../../redux/slices/guestRedux/guestBillingRedux';
import { LoadingButton } from '@mui/lab';
import InvoicePDF from '../../../../sections/@dashboard/invoice/details/InvoicePDF';
import { COMPANY_DATA_FOR_PDF } from '../../../../config';
import Iconify from '../../../../components/Iconify';
import { PDFDownloadLink } from '@react-pdf/renderer';
import moment from 'moment';
import { accessVerify } from '../../../../utils/common';

export default function GuestInvoice({ cardId, selectedData, isSecondView=false }) {
  const [showPaymentDetails, setShowPaymentDetails] = useState(false);

  const onDeclineItem = (id) => {
    dispatch(deleteGuestBilling(id));
  };
  const removeLinkedCardFromCheckin = () => {
    dispatch(removeCardFromCheckin(cardId));
  };

  const totalAmountBeforeTax = selectedData?.guests?.billing?.reduce((sum, item) => sum + item.amount, 0) || 0;
  const totalGovTax = totalAmountBeforeTax * 0.025;
  const totalAmount = totalAmountBeforeTax+totalGovTax;
  const paidAmount = selectedData?.guests?.payments?.reduce((sum, payment) => sum + payment.amount, 0) || 0;
  const balanceAmount = totalAmount - paidAmount;

  const handlePaymentClick = () => {
    setShowPaymentDetails(!showPaymentDetails);
  };

  const getItemsForInvoice = () => {
    const rowData = selectedData?.guests?.billing;
    if (selectedData?.guests?.log) {
      return rowData?.map((value, index) => {
        return {
          id: index,
          title: value?.title,
          description: value?.description,
          quantity: value?.qty,
          price: (value?.amount/value?.qty)
        };
      });
    }
  };

  return (
    <Box
      sx={{
        p: 3,
        bgcolor: 'background.paper',
        borderRadius: 2,
        boxShadow: 3,
        height: 'fit-content',
      }}
    >

      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center', marginBottom: 1 }}>
          <Box>
            <Typography variant="h6">
              {selectedData?.guests?.name} ({selectedData?.guests?.phone})
            </Typography>
            <Typography variant="body2">
              Employee: {selectedData?.guests?.user?.name}
            </Typography>
          </Box>

          <PDFDownloadLink
            document={
              <InvoicePDF
                invoice={{
                  invoiceNumber: `CheckIn-${selectedData?.guests?.id}`,
                  createDate: selectedData?.guests?.checkInDate,
                  dueDate: selectedData?.guests?.checkOutDate,
                  status: balanceAmount === 0 ? 'Fully Paid' : 'Pending Payment',
                  discount: 0,
                  taxes: 0,
                  totalAmountBeforeTax: totalAmountBeforeTax,
                  totalGovTax: totalGovTax,
                  paidAmount: paidAmount,
                  totalPrice: balanceAmount,
                  subTotalPrice: totalAmount,
                  topic: 'Received Item Details',
                  invoiceFrom: {
                    name: COMPANY_DATA_FOR_PDF?.name,
                    address: COMPANY_DATA_FOR_PDF?.address,
                    phone: COMPANY_DATA_FOR_PDF?.phone,
                  },
                  invoiceTo: {
                    name: selectedData?.guests?.name,
                    address: "blmu",
                    phone: "blmu",
                  },
                  items: getItemsForInvoice(),
                }}
              />
            }
            fileName={`INV-CheckIn-${selectedData?.guests?.id}`}
            style={{ textDecoration: 'none' }}
          >
            {() => (
              <Tooltip title='Download PDF'>
                <Button variant='contained' startIcon={<Iconify icon='eva:download-fill' />} sx={{ padding: '3px 11px' }} color={'error'}>
                  Invoice
                </Button>
              </Tooltip>
            )}
          </PDFDownloadLink>
        </Box>
      </Box>

      {selectedData?.guests?.billing?.length > 0 && (
        <>
          <Typography variant="h6" gutterBottom sx={{ pt: 2, borderTop: 1, borderColor: 'divider' }}>
            Billing Details
          </Typography>
          <Box
            sx={{
              maxHeight: isSecondView ? '60vh':'45vh',
              overflowY: 'auto',
              pr: 1,
            }}
          >
            <Grid container spacing={1}>
              {selectedData.guests.billing.map((bill, index) => {
                const billDate = new Date(bill.dateTime);
                const formattedDate = moment(bill.datetime, 'YYYY-MM-DD HH:mm:ss').format('YYYY-MM-DD');
                const formattedTime = moment(bill.datetime, 'YYYY-MM-DD HH:mm:ss').format('HH:mm');
                return (
                  <React.Fragment key={index}>
                    <Grid item xs={9}>
                      <Typography variant='body2' noWrap>
                        {bill?.title} ({bill?.category})
                      </Typography>
                      <Typography variant='caption' color='text.secondary' noWrap>
                        <b>Description:</b> {bill?.description}
                        <br />
                      </Typography>
                      <Typography variant='caption' color='text.secondary'>
                        Qty: {bill?.qty} × {fCurrency(bill?.amount / bill?.qty)}
                      </Typography>
                      <Typography variant='caption' color='text.secondary' display='block'>
                        {formattedDate} at {formattedTime} by {bill?.user?.name}
                      </Typography>
                    </Grid>
                    {accessVerify('SUPER_ADMIN') &&
                      <Grid item xs={3} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end' }}>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant='body2'>{fCurrency((bill?.amount).toFixed(2))}</Typography>
                          <IconButton size='small' color='error' onClick={() => onDeclineItem(bill?.id)} sx={{ mt: 1 }}>
                            <CloseIcon fontSize='small' />
                          </IconButton>
                        </Box>
                      </Grid>
                    }
                    {index !== selectedData.guests.billing.length - 1 && <Grid item xs={12} sx={{ my: 0.5, borderBottom: 1, borderColor: 'divider' }} />}
                  </React.Fragment>
                );
              })}
            </Grid>
          </Box>

          {/* Total Section */}
          <Box
            sx={{
              mt: 2,
              pt: 2,
              borderTop: 1,
              borderColor: 'divider',
              backgroundColor: 'action.hover',
              borderRadius: 1,
              p: 1,
            }}
          >
            <Grid container>
              <Grid item xs={6}>
                <Typography variant="subtitle1">Total Before Tax:</Typography>
              </Grid>
              <Grid item xs={6} sx={{ textAlign: 'right' }}>
                <Typography variant="subtitle1">
                  {fCurrency(totalAmountBeforeTax.toFixed(2))}
                </Typography>
              </Grid>
            </Grid>
            <Grid container>
              <Grid item xs={6}>
                <Typography variant="subtitle1">Government Tax (2.5%):</Typography>
              </Grid>
              <Grid item xs={6} sx={{ textAlign: 'right' }}>
                <Typography variant="subtitle1">
                  {fCurrency(totalGovTax.toFixed(2))}
                </Typography>
              </Grid>
            </Grid>
            <br/>
            <hr/>

            <Grid container>
              <Grid item xs={6}>
                <Typography variant="subtitle1">Total Amount:</Typography>
              </Grid>
              <Grid item xs={6} sx={{ textAlign: 'right' }}>
                <Typography variant="subtitle1">
                  {fCurrency(totalAmount.toFixed(2))}
                </Typography>
              </Grid>
            </Grid>
            <hr/>
            <br/>
            {/* Paid Amount Section */}
            <Grid container>
              <Grid item xs={6}>
                <Typography variant="subtitle1" onClick={handlePaymentClick} sx={{ cursor: 'pointer' }}>
                  Paid:
                </Typography>
              </Grid>
              <Grid item xs={6} sx={{ textAlign: 'right' }}>
                <Typography variant="subtitle1" onClick={handlePaymentClick} sx={{ cursor: 'pointer' }}>
                  {fCurrency(paidAmount.toFixed(2))}
                </Typography>
              </Grid>
            </Grid>


            {/* Payment Details Collapse */}
            <Collapse in={showPaymentDetails}>
              <Box sx={{ mt: 2, p: 1, bgcolor: 'background.default', borderRadius: 1 }}>
                {selectedData?.guests?.payments?.map((payment, index) => (
                  <Box
                    key={index}
                    sx={{
                      mb: 2,
                      p: 1,
                      border: '1px solid #ddd',
                      borderRadius: 1,
                      position: 'relative',
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography variant="body2">
                          Amount: {fCurrency(payment.amount.toFixed(2))}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Date: {new Date(payment.dateTime).toLocaleString()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {payment.paymentType} Marked By {payment?.user?.name}
                        </Typography>
                      </Box>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => {
                          console.log(payment);
                          dispatch(removePaidPayment(payment?.id));
                        }}
                      >
                        Delete
                      </Button>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Collapse>



            {/* Balance Amount Section */}
            <Grid container>
              <Grid item xs={6}>
                <Typography variant="subtitle1">Balance :</Typography>
              </Grid>
              <Grid item xs={6} sx={{ textAlign: 'right' }}>
                <Typography variant="subtitle1">
                  {balanceAmount === 0 ? 'Fully Paid' : fCurrency(balanceAmount.toFixed(2))}
                </Typography>
                {((balanceAmount < 10 || balanceAmount > -10) && Number(totalAmount) > 0) && <LoadingButton
                  type="submit"
                  variant="outlined"
                  fullWidth
                  size="small"
                  color="error"
                  onClick={() => removeLinkedCardFromCheckin()}
                >
                  Remove Card From Check In
                </LoadingButton>}
              </Grid>
            </Grid>
          </Box>
        </>
      )}
    </Box>
  );
}